import { useContext } from "react";
import type { AttributeField } from "@/features/properties/types/propertyAttributes";
import { HideAnsweredContext, HideRequiredContext, RequiredSetContext } from "./contexts";
import { isFieldAnswered, isFieldRequired } from "./surveyHelpers";
import { LeafFieldRenderer, type LeafField } from "./LeafFieldRenderer";
import { NodeSection } from "./NodeSection";

export function FieldRenderer({
  field,
  depth,
  propertyId,
  nodePath = [],
  onUpdateField,
  onUpdateFieldMetadata,
  onOpenPhotos,
}: {
  field: AttributeField;
  depth: number;
  propertyId: string;
  /** Ids of ancestor node sections, outermost first; used for upload paths. */
  nodePath?: string[];
  onUpdateField: (refId: string, value: string | number | boolean | string[] | null) => void;
  onUpdateFieldMetadata: (
    fieldId: string,
    patch: { confidence?: number | null; evidence?: string[]; notes?: string | null }
  ) => void;
  onOpenPhotos: (urls: string[], index: number, title: string) => void;
}) {
  const hideAnswered = useContext(HideAnsweredContext);
  const hideRequired = useContext(HideRequiredContext);
  const requiredSet = useContext(RequiredSetContext);

  // Hide answered leaf fields
  if (hideAnswered && field.type !== "node" && isFieldAnswered(field)) {
    return null;
  }

  // Hide field questions (required in the template)
  if (hideRequired && field.type !== "node" && isFieldRequired(field, requiredSet)) {
    return null;
  }

  if (field.type === "node") {
    return (
      <NodeSection
        node={field}
        depth={depth}
        propertyId={propertyId}
        nodePath={nodePath}
        onUpdateField={onUpdateField}
        onUpdateFieldMetadata={onUpdateFieldMetadata}
        onOpenPhotos={onOpenPhotos}
      />
    );
  }
  return (
    <LeafFieldRenderer
      field={field as LeafField}
      propertyId={propertyId}
      nodePath={nodePath}
      onUpdateField={onUpdateField}
      onUpdateFieldMetadata={onUpdateFieldMetadata}
      onOpenPhotos={onOpenPhotos}
    />
  );
}
